import type { ReactNode } from "react";

import { retryLabel, type ViewState } from "../view-state.ts";
import { MarketShell, type BadgeKind, type MarketAction } from "./MarketShell.tsx";
import { OutcomeShell, type OutcomeAction, type OutcomeKind } from "./OutcomeShell.tsx";
import { describeViewState } from "./StateScreen.tsx";

/**
 * Loading, offline, forbidden, conflict and error, in each of the two shells the second tranche adds.
 *
 * `StateScreen` already does this for `PhoneShell`, and the reasoning is written out there: none of
 * the four design files draws a loading, forbidden, conflict or offline state, the rules say build
 * every state, and a phone on a Kenyan mobile network reaches all of them. So each is rendered in
 * the shell's **own** vocabulary — its app bar, its card, its badge, its 52dp action — rather than
 * in a look nobody drew, and the words are flagged as this build's.
 *
 * The one thing these add over `StateScreen` is that there are now two shells and the state words
 * must be the same in both. They come from `describeViewState`, which is the single function the
 * booking flow already uses, so a sentence changed for one screen changes for all twenty-one.
 */

/** `ChipClass` and `BadgeKind` are two vocabularies over one idea; this is the translation. */
const BADGE_FOR: Readonly<Record<string, BadgeKind>> = {
  idle: "idle",
  active: "active",
  caution: "caution",
  closed: "closed",
  unknown: "unknown",
  done: "done",
  /*
   * `failed` has no counterpart in the commerce files' `K` table, which publishes seven kinds and
   * not that one. `closed` is the nearest: a filled square in the neutral fill, which is what those
   * files draw for a thing that cannot be acted on. Mapped here rather than at a call site so there
   * is one place to change if a `failed` kind is ever added to those files.
   */
  failed: "closed",
};

export interface TrancheStateProps {
  readonly state: ViewState;
  readonly onRetry?: () => void;
  readonly onBack?: () => void;
  /** What the screen was trying to do, said in the sub-line of the loading state. */
  readonly purpose: string;
  /** The app bar's own two lines, which are the screen's rather than the state's. */
  readonly barTitle: string;
  readonly barSub: string;
}

/** A state of one of the eight commerce, money and account screens. */
export function MarketState(props: TrancheStateProps): ReactNode {
  const drawn = describeViewState(props.state, props.purpose);
  const retry = retryLabel(props.state);
  const actions: MarketAction[] =
    retry === undefined || props.onRetry === undefined
      ? []
      : [{ label: retry, glyph: "refresh", primary: true, onPress: props.onRetry }];

  return (
    <MarketShell
      screen="market--state"
      left={{ glyph: "arrow_back", label: "Back", ...(props.onBack && { onPress: props.onBack }) }}
      barTitle={props.barTitle}
      barSub={props.barSub}
      right={{ glyph: "help", label: "Help" }}
      badge={{ kind: BADGE_FOR[drawn.chip.class] ?? "unknown", label: drawn.chip.label }}
      title={drawn.title}
      sub={drawn.sub}
      flag="wording not in the design"
      actions={actions}
    />
  );
}

/** The same, in the payment-outcome shell. */
export function OutcomeState(props: TrancheStateProps): ReactNode {
  const drawn = describeViewState(props.state, props.purpose);
  const retry = retryLabel(props.state);
  const actions: OutcomeAction[] =
    retry === undefined || props.onRetry === undefined
      ? []
      : [{ label: retry, glyph: "refresh", primary: true, onPress: props.onRetry }];

  return (
    <OutcomeShell
      screen="outcome--state"
      {...(props.onBack && { onBack: props.onBack })}
      subject={props.barSub}
      kind={KIND_FOR[drawn.chip.class] ?? "unknown"}
      heroGlyph={GLYPH_FOR[drawn.chip.class] ?? "help"}
      chip={drawn.chip.label}
      title={drawn.title}
      sub={drawn.sub}
      flag="wording not in the design"
      instrument={{ glyph: "smartphone", nameAbsent: "Not read yet", amountAbsent: "—" }}
      blocks={[]}
      actions={actions}
    />
  );
}

/**
 * The payment file's four kinds against the seven chip classes, which is a narrower translation.
 *
 * `07 Payment Outcome Unknown.dc.html` publishes exactly four: `wait`, `unknown`, `done` and
 * `failed`. Everything this application does not know is `unknown` — the dashed ring with the `?` —
 * because on a payment screen the one thing that must never be guessed is which way it went.
 */
const KIND_FOR: Readonly<Record<string, OutcomeKind>> = {
  idle: "wait",
  active: "wait",
  caution: "unknown",
  closed: "failed",
  unknown: "unknown",
  done: "done",
  failed: "failed",
};

/** The hero glyph per kind, taken from the design's own five states. */
const GLYPH_FOR: Readonly<Record<string, string>> = {
  idle: "hourglass_top",
  active: "hourglass_top",
  caution: "help",
  closed: "close",
  unknown: "help",
  done: "check",
  failed: "close",
};
